import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../../trpc/trpc';
import { useRealtimeEvent } from '../../../../realtime/useRealtimeEvent';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { isBoardMaking, PostsBoard, PostsPlatform } from '../postsModel';

/** The poll while a file is made and the socket is down (owner rule: no poll while connected). */
export const POSTS_BOARD_POLL_MS = 15 * 1000;

export interface PostsBoardQuery {
  board: PostsBoard | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * `assets.posts` of the artist (or of one song, the release page Posts tab), design assets-rethink
 * c1. The kit asset, reel and post events read the board and the archive again.
 */
export function usePostsBoard(input: {
  accountId: number;
  catalogItemUuid: string | undefined;
  platform: PostsPlatform | undefined;
}): PostsBoardQuery {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();
  const pollMs = useRealtimePollInterval(POSTS_BOARD_POLL_MS);
  const query = useQuery({
    ...trpcAPI.assets.posts.queryOptions(
      {
        accountId: input.accountId,
        catalogItemUuid: input.catalogItemUuid ?? null,
        platform: input.platform ?? null,
      },
      { retry: false }
    ),
    refetchInterval: (state) => (pollMs > 0 && isBoardMaking(state.state.data) ? pollMs : false),
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: trpcAPI.assets.posts.pathKey() }).catch(console.error);
    queryClient.invalidateQueries({ queryKey: trpcAPI.assets.archived.pathKey() }).catch(console.error);
  }, [queryClient, trpcAPI]);
  useRealtimeEvent('kit.asset.updated', refresh);
  useRealtimeEvent('reel.updated', refresh);
  useRealtimeEvent('promo.post.updated', refresh);
  useRealtimeEvent('promo.posts.changed', refresh);

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(console.error);
  }, [refetchQuery]);

  return { board: query.data, isLoading: query.isLoading, error: query.error ?? undefined, refetch };
}
